import React, { useState, useEffect, useCallback } from 'react';
import { X, Trash2, RefreshCw, Search, Database, Users, FileText, BarChart3, ChevronDown, ChevronUp, Clock, Download, Eye, Cloud, CloudUpload, Play, Square, Loader2, CheckCircle2, AlertCircle, Zap } from 'lucide-react';

/**
 * TrainingDataViewer - 저장된 학습 데이터 조회/삭제 모달
 */
export default function TrainingDataViewer({ isOpen, onClose, apiBaseUrl }) {
  const [stats, setStats] = useState(null);
  const [dataList, setDataList] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [filterVendor, setFilterVendor] = useState('');
  const [filterDocType, setFilterDocType] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [detailData, setDetailData] = useState(null);
  const [page, setPage] = useState(0);
  const [detailViewId, setDetailViewId] = useState(null);
  const [detailViewData, setDetailViewData] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [activeSection, setActiveSection] = useState('data'); // 'data' | 'finetuning'
  const [gcsStats, setGcsStats] = useState(null);
  const [gcsLoading, setGcsLoading] = useState(false);
  const [ftJobs, setFtJobs] = useState([]);
  const [ftJobsLoading, setFtJobsLoading] = useState(false);
  const [ftStarting, setFtStarting] = useState(false);
  const [ftConfig, setFtConfig] = useState({
    displayName: '',
    baseModel: 'gemini-2.5-flash',
    epochCount: 3,
    learningRateMultiplier: 1.0,
    adapterSize: 4
  });
  const [rebuilding, setRebuilding] = useState(false);
  const PAGE_SIZE = 20;

  const fetchStats = useCallback(async () => {
    try {
      const res = await fetch(`${apiBaseUrl}/api/training-data/stats`);
      const data = await res.json();
      if (data.success) setStats(data);
    } catch (err) {
      console.error('Stats fetch error:', err);
    }
  }, [apiBaseUrl]);

  const fetchGcsStats = useCallback(async () => {
    setGcsLoading(true);
    try {
      const res = await fetch(`${apiBaseUrl}/api/docuai-finetuning/gcs-stats`);
      const data = await res.json();
      if (data.success !== false) setGcsStats(data);
    } catch (err) {
      console.error('GCS stats fetch error:', err);
    } finally {
      setGcsLoading(false);
    }
  }, [apiBaseUrl]);

  const fetchFtJobs = useCallback(async () => {
    setFtJobsLoading(true);
    try {
      const res = await fetch(`${apiBaseUrl}/api/docuai-finetuning/jobs`);
      const data = await res.json();
      if (data.success) setFtJobs(data.jobs || []);
    } catch (err) {
      console.error('FT jobs fetch error:', err);
    } finally {
      setFtJobsLoading(false);
    }
  }, [apiBaseUrl]);

  const handleRebuildJsonl = async () => {
    if (!window.confirm('DB 학습 데이터 기반으로 GCS JSONL을 전체 재생성합니다. 진행하시겠습니까?')) return;
    setRebuilding(true);
    try {
      const res = await fetch(`${apiBaseUrl}/api/docuai-finetuning/rebuild-jsonl`, { method: 'POST' });
      const data = await res.json();
      if (data.success) {
        alert(`JSONL 재생성 완료: ${data.count || 0}건`);
        fetchGcsStats();
      } else {
        alert(`재생성 실패: ${data.error}`);
      }
    } catch (err) {
      alert(`오류: ${err.message}`);
    } finally {
      setRebuilding(false);
    }
  };

  const handleStartFineTuning = async () => {
    const name = ftConfig.displayName.trim() || `docuai-ft-${Date.now()}`;
    if (!window.confirm(`파인튜닝을 시작합니다.\n\n모델: ${ftConfig.baseModel}\nEpoch: ${ftConfig.epochCount}\n이름: ${name}\n\n진행하시겠습니까?`)) return;
    setFtStarting(true);
    try {
      const res = await fetch(`${apiBaseUrl}/api/docuai-finetuning/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...ftConfig, displayName: name })
      });
      const data = await res.json();
      if (data.success) {
        alert(`파인튜닝 작업 생성 완료!\nJob ID: ${data.jobId}`);
        fetchFtJobs();
      } else {
        alert(`실패: ${data.error}`);
      }
    } catch (err) {
      alert(`오류: ${err.message}`);
    } finally {
      setFtStarting(false);
    }
  };

  const handleCancelJob = async (jobName) => {
    if (!window.confirm('이 파인튜닝 작업을 취소하시겠습니까?')) return;
    try {
      const res = await fetch(`${apiBaseUrl}/api/docuai-finetuning/jobs/${encodeURIComponent(jobName)}/cancel`, { method: 'POST' });
      const data = await res.json();
      if (data.success) {
        alert('작업이 취소되었습니다.');
        fetchFtJobs();
      } else {
        alert(`취소 실패: ${data.error}`);
      }
    } catch (err) {
      alert(`오류: ${err.message}`);
    }
  };

  const fetchList = useCallback(async (resetPage = false) => {
    setLoading(true);
    const currentPage = resetPage ? 0 : page;
    if (resetPage) setPage(0);
    try {
      const params = new URLSearchParams({
        limit: PAGE_SIZE,
        offset: currentPage * PAGE_SIZE
      });
      if (filterVendor.trim()) params.append('vendorName', filterVendor.trim());
      if (filterDocType.trim()) params.append('documentType', filterDocType.trim());

      const res = await fetch(`${apiBaseUrl}/api/training-data/list?${params}`);
      const data = await res.json();
      if (data.success !== false) {
        setDataList(data.data || []);
        setTotal(data.total || 0);
      }
    } catch (err) {
      console.error('List fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [apiBaseUrl, filterVendor, filterDocType, page]);

  useEffect(() => {
    if (isOpen) {
      fetchStats();
      fetchList(true);
      fetchGcsStats();
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && activeSection === 'finetuning') {
      fetchGcsStats();
      fetchFtJobs();
    }
  }, [isOpen, activeSection]);

  useEffect(() => {
    if (isOpen) fetchList();
  }, [page]);

  const handleDelete = async (id) => {
    if (!window.confirm('이 학습 데이터를 삭제하시겠습니까?')) return;
    try {
      const res = await fetch(`${apiBaseUrl}/api/training-data/${id}`, { method: 'DELETE' });
      const data = await res.json();
      if (data.success) {
        fetchList();
        fetchStats();
        if (expandedId === id) {
          setExpandedId(null);
          setDetailData(null);
        }
      }
    } catch (err) {
      console.error('Delete error:', err);
    }
  };

  const handleExpand = (id) => {
    if (expandedId === id) {
      setExpandedId(null);
      setDetailData(null);
      return;
    }
    setExpandedId(id);
    setDetailData(dataList.find(d => d.id === id));
  };

  const handleViewDetail = async (id) => {
    try {
      const res = await fetch(`${apiBaseUrl}/api/training-data/${id}`);
      const data = await res.json();
      if (data.success) {
        setDetailViewId(id);
        setDetailViewData(data.data);
      }
    } catch (err) {
      console.error('Detail fetch error:', err);
    }
  };

  const handleExportJsonl = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (filterVendor.trim()) params.append('vendorName', filterVendor.trim());
      if (filterDocType.trim()) params.append('documentType', filterDocType.trim());

      const res = await fetch(`${apiBaseUrl}/api/training-data/export-jsonl?${params}`);
      if (!res.ok) {
        const err = await res.json();
        alert(err.error || 'JSONL 내보내기 실패');
        return;
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `docuai_finetuning_${new Date().toISOString().slice(0, 10)}.jsonl`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export JSONL error:', err);
      alert('JSONL 내보내기 중 오류가 발생했습니다.');
    } finally {
      setExporting(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  };

  const qualityColor = (score) => {
    if (score >= 90) return 'text-[#1f1f1f] bg-[#f1f3f4]';
    if (score >= 70) return 'text-[#444746] bg-[#f1f3f4]';
    return 'text-[#5f6368] bg-[#e8eaed]';
  };

  if (!isOpen) return null;

  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/30" onClick={onClose}>
      <div
        className="bg-white rounded-lg shadow-2xl w-[780px] max-h-[85vh] flex flex-col border border-gray-200"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 bg-gray-50 rounded-t-lg">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <Database className="w-4.5 h-4.5 text-[#444746]" />
              <h2 className="text-sm font-bold text-gray-800">학습 데이터 관리</h2>
            </div>
            {/* Tab Buttons */}
            <div className="flex items-center bg-gray-200 rounded-md p-0.5">
              <button
                onClick={() => setActiveSection('data')}
                className={`flex items-center gap-1 px-2.5 py-1 rounded text-[11px] font-bold transition-colors ${
                  activeSection === 'data' ? 'bg-white text-[#1f1f1f] shadow-sm' : 'text-[#5f6368] hover:text-[#444746]'
                }`}
              >
                <FileText className="w-3 h-3" /> 데이터
              </button>
              <button
                onClick={() => setActiveSection('finetuning')}
                className={`flex items-center gap-1 px-2.5 py-1 rounded text-[11px] font-bold transition-colors ${
                  activeSection === 'finetuning' ? 'bg-white text-[#1f1f1f] shadow-sm' : 'text-[#5f6368] hover:text-[#444746]'
                }`}
              >
                <Zap className="w-3 h-3" /> 파인튜닝
              </button>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {activeSection === 'data' && (
              <button
                onClick={handleExportJsonl}
                disabled={exporting || total === 0}
                className={`flex items-center gap-1 px-2.5 py-1 rounded text-[11px] font-bold transition-colors ${
                  exporting ? 'bg-[#e8eaed] text-[#5f6368] cursor-wait' :
                  total === 0 ? 'bg-[#e8eaed] text-[#dadce0] cursor-not-allowed' :
                  'bg-[#444746] text-white hover:bg-[#1f1f1f] shadow-sm'
                }`}
                title="Vertex AI 파인튜닝용 JSONL 다운로드 (현재 필터 적용)"
              >
                <Download className="w-3 h-3" />
                {exporting ? '내보내는 중...' : 'JSONL 내보내기'}
              </button>
            )}
            <button onClick={onClose} className="p-1 hover:bg-gray-200 rounded transition-colors">
              <X className="w-4 h-4 text-gray-500" />
            </button>
          </div>
        </div>

        {/* ══════ Fine-Tuning Section ══════ */}
        {activeSection === 'finetuning' && (
          <div className="flex-1 overflow-y-auto min-h-0">
            {/* GCS Dataset Status */}
            <div className="px-5 py-4 border-b border-gray-100">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Cloud className="w-4 h-4 text-[#5f6368]" />
                  <span className="text-xs font-bold text-gray-800">GCS JSONL 데이터셋</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <button
                    onClick={handleRebuildJsonl}
                    disabled={rebuilding}
                    className={`flex items-center gap-1 px-2.5 py-1 rounded text-[11px] font-bold transition-colors ${
                      rebuilding ? 'bg-[#e8eaed] text-[#5f6368] cursor-wait' : 'bg-[#444746] text-white hover:bg-[#1f1f1f] shadow-sm'
                    }`}
                    title="DB 학습 데이터 기반으로 JSONL 전체 재생성"
                  >
                    {rebuilding ? <Loader2 className="w-3 h-3 animate-spin" /> : <CloudUpload className="w-3 h-3" />}
                    {rebuilding ? '재생성 중...' : 'JSONL 재빌드'}
                  </button>
                  <button
                    onClick={fetchGcsStats}
                    disabled={gcsLoading}
                    className="p-1 hover:bg-gray-100 rounded transition-colors"
                    title="새로고침"
                  >
                    <RefreshCw className={`w-3.5 h-3.5 text-gray-400 ${gcsLoading ? 'animate-spin' : ''}`} />
                  </button>
                </div>
              </div>
              {gcsStats ? (
                <div className="grid grid-cols-3 gap-3">
                  <div className="bg-white rounded-lg border border-gray-200 px-3 py-2.5">
                    <div className="flex items-center gap-1.5 mb-1">
                      {gcsStats.exists ? <CheckCircle2 className="w-3.5 h-3.5 text-[#444746]" /> : <AlertCircle className="w-3.5 h-3.5 text-[#dadce0]" />}
                      <span className="text-[10px] font-bold text-gray-500">상태</span>
                    </div>
                    <div className={`text-sm font-bold ${gcsStats.exists ? 'text-[#1f1f1f]' : 'text-[#5f6368]'}`}>
                      {gcsStats.exists ? '활성' : '미생성'}
                    </div>
                  </div>
                  <div className="bg-white rounded-lg border border-gray-200 px-3 py-2.5">
                    <div className="text-[10px] font-bold text-gray-500 mb-1">학습 샘플 수</div>
                    <div className="text-lg font-bold text-[#1f1f1f]">{gcsStats.entryCount || 0}</div>
                  </div>
                  <div className="bg-white rounded-lg border border-gray-200 px-3 py-2.5">
                    <div className="text-[10px] font-bold text-gray-500 mb-1">파일 크기</div>
                    <div className="text-sm font-bold text-gray-700">
                      {gcsStats.size ? `${(gcsStats.size / 1024).toFixed(1)} KB` : '-'}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-xs text-gray-400 text-center py-4">GCS 통계 로딩 중...</div>
              )}
              {gcsStats?.location && (
                <div className="mt-2 text-[10px] text-gray-400 font-mono truncate" title={gcsStats.location}>
                  {gcsStats.location}
                </div>
              )}
            </div>

            {/* Fine-Tuning Config */}
            <div className="px-5 py-4 border-b border-gray-100">
              <div className="flex items-center gap-2 mb-3">
                <Zap className="w-4 h-4 text-[#5f6368]" />
                <span className="text-xs font-bold text-[#1f1f1f]">파인튜닝 설정</span>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] font-bold text-gray-500 block mb-1">작업 이름</label>
                  <input
                    type="text"
                    value={ftConfig.displayName}
                    onChange={e => setFtConfig(p => ({ ...p, displayName: e.target.value }))}
                    placeholder={`docuai-ft-${new Date().toISOString().slice(0, 10)}`}
                    className="w-full text-xs px-2.5 py-1.5 border border-[#dadce0] rounded focus:ring-1 focus:ring-[#5f6368] focus:border-[#5f6368] outline-none"
                  />
                </div>
                <div>
                  <label className="text-[10px] font-bold text-gray-500 block mb-1">기본 모델</label>
                  <select
                    value={ftConfig.baseModel}
                    onChange={e => setFtConfig(p => ({ ...p, baseModel: e.target.value }))}
                    className="w-full text-xs px-2.5 py-1.5 border border-[#dadce0] rounded focus:ring-1 focus:ring-[#5f6368] focus:border-[#5f6368] outline-none bg-white"
                  >
                    <option value="gemini-2.5-flash">DocuAI-2.5</option>
                    <option value="gemini-2.5-pro">DocuAI-2.5 Pro</option>
                    <option value="gemini-2.0-flash-001">DocuAI-2.0</option>
                    <option value="gemini-1.5-flash-002">DocuAI-1.5</option>
                  </select>
                </div>
                <div>
                  <label className="text-[10px] font-bold text-gray-500 block mb-1">Epoch 수</label>
                  <input
                    type="number"
                    min={1}
                    max={20}
                    value={ftConfig.epochCount}
                    onChange={e => setFtConfig(p => ({ ...p, epochCount: parseInt(e.target.value) || 3 }))}
                    className="w-full text-xs px-2.5 py-1.5 border border-[#dadce0] rounded focus:ring-1 focus:ring-[#5f6368] focus:border-[#5f6368] outline-none"
                  />
                </div>
                <div>
                  <label className="text-[10px] font-bold text-gray-500 block mb-1">Learning Rate 배수</label>
                  <input
                    type="number"
                    min={0.1}
                    max={10}
                    step={0.1}
                    value={ftConfig.learningRateMultiplier}
                    onChange={e => setFtConfig(p => ({ ...p, learningRateMultiplier: parseFloat(e.target.value) || 1.0 }))}
                    className="w-full text-xs px-2.5 py-1.5 border border-[#dadce0] rounded focus:ring-1 focus:ring-[#5f6368] focus:border-[#5f6368] outline-none"
                  />
                </div>
              </div>
              <div className="mt-3 flex items-center justify-end gap-2">
                <button
                  onClick={handleStartFineTuning}
                  disabled={ftStarting || !gcsStats?.exists || gcsStats?.entryCount === 0}
                  className={`flex items-center gap-1.5 px-4 py-1.5 rounded text-xs font-bold transition-colors ${
                    ftStarting ? 'bg-[#e8eaed] text-[#5f6368] cursor-wait' :
                    (!gcsStats?.exists || gcsStats?.entryCount === 0) ? 'bg-[#e8eaed] text-[#dadce0] cursor-not-allowed' :
                    'bg-[#444746] text-white hover:bg-[#1f1f1f] shadow-sm'
                  }`}
                >
                  {ftStarting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />}
                  {ftStarting ? '시작 중...' : '파인튜닝 시작'}
                </button>
              </div>
            </div>

            {/* Fine-Tuning Jobs List */}
            <div className="px-5 py-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <BarChart3 className="w-4 h-4 text-[#5f6368]" />
                  <span className="text-xs font-bold text-gray-800">파인튜닝 작업 목록</span>
                  <span className="text-[10px] bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded font-bold">{ftJobs.length}</span>
                </div>
                <button
                  onClick={fetchFtJobs}
                  disabled={ftJobsLoading}
                  className="p-1 hover:bg-gray-100 rounded transition-colors"
                >
                  <RefreshCw className={`w-3.5 h-3.5 text-gray-400 ${ftJobsLoading ? 'animate-spin' : ''}`} />
                </button>
              </div>
              {ftJobsLoading && ftJobs.length === 0 ? (
                <div className="text-xs text-gray-400 text-center py-8">
                  <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2 text-gray-300" />
                  작업 목록 로딩 중...
                </div>
              ) : ftJobs.length === 0 ? (
                <div className="text-xs text-gray-400 text-center py-8">
                  <Zap className="w-8 h-8 mx-auto mb-2 text-gray-200" />
                  파인튜닝 작업이 없습니다.
                </div>
              ) : (
                <div className="space-y-2">
                  {ftJobs.map(job => {
                    const stateColors = {
                      'JOB_STATE_SUCCEEDED': 'bg-[#f1f3f4] border-[#dadce0] text-[#1f1f1f]',
                      'JOB_STATE_RUNNING': 'bg-[#f1f3f4] border-[#5f6368] text-[#1f1f1f]',
                      'JOB_STATE_PENDING': 'bg-[#f1f3f4] border-[#dadce0] text-[#444746]',
                      'JOB_STATE_FAILED': 'bg-[#f1f3f4] border-[#dadce0] text-[#5f6368]',
                      'JOB_STATE_CANCELLED': 'bg-[#f1f3f4] border-[#e8eaed] text-[#5f6368]',
                      'JOB_STATE_CANCELLING': 'bg-[#f1f3f4] border-[#e8eaed] text-[#5f6368]'
                    };
                    const stateLabels = {
                      'JOB_STATE_SUCCEEDED': '완료',
                      'JOB_STATE_RUNNING': '실행 중',
                      'JOB_STATE_PENDING': '대기 중',
                      'JOB_STATE_FAILED': '실패',
                      'JOB_STATE_CANCELLED': '취소됨',
                      'JOB_STATE_CANCELLING': '취소 중'
                    };
                    const stateIcons = {
                      'JOB_STATE_SUCCEEDED': <CheckCircle2 className="w-3.5 h-3.5" />,
                      'JOB_STATE_RUNNING': <Loader2 className="w-3.5 h-3.5 animate-spin" />,
                      'JOB_STATE_PENDING': <Clock className="w-3.5 h-3.5" />,
                      'JOB_STATE_FAILED': <AlertCircle className="w-3.5 h-3.5" />,
                      'JOB_STATE_CANCELLED': <Square className="w-3.5 h-3.5" />,
                      'JOB_STATE_CANCELLING': <Square className="w-3.5 h-3.5" />
                    };
                    const colorCls = stateColors[job.state] || 'bg-[#f1f3f4] border-[#dadce0] text-[#5f6368]';
                    const isActive = job.state === 'JOB_STATE_RUNNING' || job.state === 'JOB_STATE_PENDING';

                    return (
                      <div key={job.jobId} className={`rounded-lg border px-3 py-2.5 ${colorCls}`}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            {stateIcons[job.state] || <Clock className="w-3.5 h-3.5" />}
                            <span className="text-xs font-bold">{job.displayName || job.jobId}</span>
                            <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-white/50">
                              {stateLabels[job.state] || job.state}
                            </span>
                          </div>
                          {isActive && (
                            <button
                              onClick={() => handleCancelJob(job.jobName)}
                              className="flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold bg-white/70 hover:bg-white border border-current transition-colors"
                            >
                              <Square className="w-2.5 h-2.5" /> 취소
                            </button>
                          )}
                        </div>
                        <div className="flex gap-4 mt-1.5 text-[10px] opacity-80">
                          <span>ID: {job.jobId}</span>
                          {job.baseModel && <span>모델: {job.baseModel.split('/').pop()}</span>}
                          {job.createTime && <span>생성: {formatDate(job.createTime)}</span>}
                          {job.endTime && <span>완료: {formatDate(job.endTime)}</span>}
                        </div>
                        {job.tunedModel && (
                          <div className="mt-1.5 text-[10px] font-mono bg-white/50 px-2 py-1 rounded truncate">
                            튜닝 모델: {typeof job.tunedModel === 'object' ? (job.tunedModel.model || job.tunedModel.endpoint || JSON.stringify(job.tunedModel)) : job.tunedModel}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ══════ Data Section ══════ */}
        {activeSection === 'data' && <>
        {/* Stats Cards */}
        {stats && (
          <div className="px-5 py-3 border-b border-gray-100 bg-gray-50/50">
            <div className="grid grid-cols-5 gap-3">
              <div className="bg-white rounded-lg border border-gray-200 px-3 py-2 text-center">
                <div className="text-lg font-bold text-gray-800">{stats.totalSamples || 0}</div>
                <div className="text-[10px] text-gray-500 font-medium flex items-center justify-center gap-1">
                  <FileText className="w-3 h-3" /> 총 샘플
                </div>
              </div>
              <div className="bg-white rounded-lg border border-gray-200 px-3 py-2 text-center">
                <div className="text-lg font-bold text-[#1f1f1f]">{stats.uniqueVendors || 0}</div>
                <div className="text-[10px] text-gray-500 font-medium flex items-center justify-center gap-1">
                  <Users className="w-3 h-3" /> 거래처
                </div>
              </div>
              <div className="bg-white rounded-lg border border-gray-200 px-3 py-2 text-center">
                <div className="text-lg font-bold text-[#1f1f1f]">{stats.docTypes || 0}</div>
                <div className="text-[10px] text-gray-500 font-medium flex items-center justify-center gap-1">
                  <FileText className="w-3 h-3" /> 문서유형
                </div>
              </div>
              <div className="bg-white rounded-lg border border-gray-200 px-3 py-2 text-center">
                <div className="text-lg font-bold text-[#1f1f1f]">{stats.avgQuality || 0}%</div>
                <div className="text-[10px] text-gray-500 font-medium flex items-center justify-center gap-1">
                  <BarChart3 className="w-3 h-3" /> 평균 품질
                </div>
              </div>
              <div className="bg-white rounded-lg border border-gray-200 px-3 py-2 text-center">
                <div className="text-lg font-bold text-gray-600">{stats.totalItems || 0}</div>
                <div className="text-[10px] text-gray-500 font-medium flex items-center justify-center gap-1">
                  <BarChart3 className="w-3 h-3" /> 총 행수
                </div>
              </div>
            </div>

            {/* Vendor breakdown */}
            {stats.vendors && stats.vendors.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {stats.vendors.slice(0, 10).map((v, i) => (
                  <button
                    key={i}
                    onClick={() => { setFilterVendor(v.vendor_name || ''); fetchList(true); }}
                    className="text-[10px] px-2 py-0.5 bg-white border border-[#dadce0] rounded-full hover:bg-[#f1f3f4] hover:border-[#5f6368] transition-colors text-[#5f6368]"
                  >
                    {v.vendor_name || '(미지정)'} <span className="font-bold text-[#1f1f1f]">{v.sample_count}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Filter Bar */}
        <div className="px-5 py-2 border-b border-gray-200 flex items-center gap-2 bg-white">
          <Search className="w-3.5 h-3.5 text-gray-400 shrink-0" />
          <input
            type="text"
            value={filterVendor}
            onChange={e => setFilterVendor(e.target.value)}
            placeholder="거래처 검색..."
            className="flex-1 text-xs px-2 py-1 border border-[#dadce0] rounded focus:ring-1 focus:ring-[#5f6368] focus:border-[#5f6368] outline-none"
            onKeyDown={e => e.key === 'Enter' && fetchList(true)}
          />
          <input
            type="text"
            value={filterDocType}
            onChange={e => setFilterDocType(e.target.value)}
            placeholder="문서유형..."
            className="w-36 text-xs px-2 py-1 border border-[#dadce0] rounded focus:ring-1 focus:ring-[#5f6368] focus:border-[#5f6368] outline-none"
            onKeyDown={e => e.key === 'Enter' && fetchList(true)}
          />
          <button
            onClick={() => fetchList(true)}
            className="px-2.5 py-1 bg-gray-800 text-white rounded text-xs font-bold hover:bg-gray-900 transition-colors flex items-center gap-1"
          >
            <Search className="w-3 h-3" /> 검색
          </button>
          <button
            onClick={() => { setFilterVendor(''); setFilterDocType(''); setTimeout(() => fetchList(true), 50); }}
            className="px-2 py-1 text-gray-500 hover:bg-gray-100 rounded text-xs transition-colors"
          >
            초기화
          </button>
          <button
            onClick={() => { fetchList(); fetchStats(); }}
            className="p-1 hover:bg-gray-100 rounded transition-colors"
            title="새로고침"
          >
            <RefreshCw className={`w-3.5 h-3.5 text-gray-400 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {/* Data List */}
        <div className="flex-1 overflow-y-auto min-h-0">
          {loading && dataList.length === 0 ? (
            <div className="flex items-center justify-center py-16 text-gray-400 text-sm">
              <RefreshCw className="w-5 h-5 animate-spin mr-2" /> 로딩 중...
            </div>
          ) : dataList.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-gray-400">
              <Database className="w-10 h-10 opacity-20 mb-3" />
              <span className="text-sm">저장된 학습 데이터가 없습니다.</span>
              <span className="text-xs mt-1">그리드에서 "학습저장" 버튼을 눌러 데이터를 저장하세요.</span>
            </div>
          ) : (
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-gray-50 z-10">
                <tr className="border-b border-gray-200">
                  <th className="px-3 py-2 text-left font-semibold text-gray-600 w-8">#</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-600">거래처</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-600">문서유형</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-600">파일명</th>
                  <th className="px-3 py-2 text-center font-semibold text-gray-600 w-14">행수</th>
                  <th className="px-3 py-2 text-center font-semibold text-gray-600 w-14">수정</th>
                  <th className="px-3 py-2 text-center font-semibold text-gray-600 w-16">품질</th>
                  <th className="px-3 py-2 text-center font-semibold text-gray-600 w-10">GCS</th>
                  <th className="px-3 py-2 text-center font-semibold text-gray-600 w-28">저장일시</th>
                  <th className="px-3 py-2 text-center font-semibold text-gray-600 w-16">작업</th>
                </tr>
              </thead>
              <tbody>
                {dataList.map((item, idx) => (
                  <React.Fragment key={item.id}>
                    <tr
                      className={`border-b border-gray-100 hover:bg-[#f1f3f4] cursor-pointer transition-colors ${expandedId === item.id ? 'bg-[#f1f3f4]' : ''}`}
                      onClick={() => handleExpand(item.id)}
                    >
                      <td className="px-3 py-1.5 text-gray-400">{page * PAGE_SIZE + idx + 1}</td>
                      <td className="px-3 py-1.5 font-medium text-gray-800 max-w-[140px] truncate" title={item.vendor_name}>
                        {item.vendor_name || <span className="text-gray-400 italic">미지정</span>}
                      </td>
                      <td className="px-3 py-1.5 text-gray-600 max-w-[100px] truncate" title={item.document_type}>
                        {item.document_type}
                      </td>
                      <td className="px-3 py-1.5 text-gray-500 max-w-[140px] truncate font-mono" title={item.file_name}>
                        {item.file_name || '-'}
                      </td>
                      <td className="px-3 py-1.5 text-center text-gray-600">{item.item_count || 0}</td>
                      <td className="px-3 py-1.5 text-center">
                        {item.correction_count > 0
                          ? <span className="text-[#1f1f1f] font-bold">{item.correction_count}</span>
                          : <span className="text-[#dadce0]">0</span>
                        }
                      </td>
                      <td className="px-3 py-1.5 text-center">
                        <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-bold ${qualityColor(item.quality_score)}`}>
                          {parseFloat(item.quality_score || 0).toFixed(0)}%
                        </span>
                      </td>
                      <td className="px-3 py-1.5 text-center">
                        {item.gcs_uri ? (
                          <Cloud className="w-3.5 h-3.5 text-[#444746] mx-auto" title={item.gcs_uri} />
                        ) : (
                          <span className="text-gray-200">-</span>
                        )}
                      </td>
                      <td className="px-3 py-1.5 text-center text-gray-400 flex items-center justify-center gap-1">
                        <Clock className="w-3 h-3" />
                        {formatDate(item.created_at)}
                      </td>
                      <td className="px-3 py-1.5 text-center">
                        <div className="flex items-center justify-center gap-1">
                          <button
                            onClick={(e) => { e.stopPropagation(); handleExpand(item.id); }}
                            className="p-0.5 hover:bg-gray-200 rounded transition-colors"
                            title="필드 목록"
                          >
                            {expandedId === item.id
                              ? <ChevronUp className="w-3.5 h-3.5 text-gray-500" />
                              : <ChevronDown className="w-3.5 h-3.5 text-gray-500" />
                            }
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleViewDetail(item.id); }}
                            className="p-0.5 hover:bg-[#f1f3f4] rounded transition-colors"
                            title="검증 데이터 보기"
                          >
                            <Eye className="w-3.5 h-3.5 text-[#5f6368] hover:text-[#1f1f1f]" />
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleDelete(item.id); }}
                            className="p-0.5 hover:bg-[#f1f3f4] rounded transition-colors"
                            title="삭제"
                          >
                            <Trash2 className="w-3.5 h-3.5 text-[#5f6368] hover:text-[#1f1f1f]" />
                          </button>
                        </div>
                      </td>
                    </tr>

                    {/* Expanded Detail */}
                    {expandedId === item.id && (
                      <tr>
                        <td colSpan={10} className="px-4 py-3 bg-gray-50/80 border-b border-gray-200">
                          <div className="space-y-2">
                            {/* Field Names */}
                            {item.field_names && (
                              <div>
                                <span className="text-[10px] font-bold text-gray-500 uppercase">필드 목록:</span>
                                <div className="flex flex-wrap gap-1 mt-1">
                                  {(typeof item.field_names === 'string' ? JSON.parse(item.field_names) : item.field_names).map((f, i) => (
                                    <span key={i} className="text-[10px] px-1.5 py-0.5 bg-white border border-gray-200 rounded text-gray-600 font-mono">
                                      {f}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}
                            {/* Meta Info */}
                            <div className="flex gap-4 text-[10px] text-gray-500">
                              <span><b>ID:</b> {item.id}</span>
                              <span><b>템플릿:</b> {item.template_id || '-'}</span>
                              <span><b>페이지:</b> {item.page_count || 1}</span>
                              <span><b>일치율:</b> {item.match_rate != null ? `${parseFloat(item.match_rate).toFixed(1)}%` : '-'}</span>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Footer with pagination */}
        <div className="px-5 py-2.5 border-t border-gray-200 bg-gray-50 rounded-b-lg flex items-center justify-between">
          <span className="text-[11px] text-gray-500">
            총 <b className="text-gray-800">{total}</b>건
            {totalPages > 1 && ` · 페이지 ${page + 1}/${totalPages}`}
          </span>
          <div className="flex items-center gap-1.5">
            {totalPages > 1 && (
              <>
                <button
                  onClick={() => setPage(Math.max(0, page - 1))}
                  disabled={page === 0}
                  className={`px-2 py-0.5 rounded text-xs font-medium transition-colors ${page === 0 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-600 hover:bg-gray-200'}`}
                >
                  ◀ 이전
                </button>
                <button
                  onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
                  disabled={page >= totalPages - 1}
                  className={`px-2 py-0.5 rounded text-xs font-medium transition-colors ${page >= totalPages - 1 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-600 hover:bg-gray-200'}`}
                >
                  다음 ▶
                </button>
              </>
            )}
          </div>
        </div>
        </>}
      </div>

      {/* Detail View Modal (verified_data) */}
      {detailViewId && detailViewData && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/40" onClick={() => { setDetailViewId(null); setDetailViewData(null); }}>
          <div className="bg-white rounded-lg shadow-2xl w-[700px] max-h-[80vh] flex flex-col border border-gray-200" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-200 bg-gray-50 rounded-t-lg">
              <div className="flex items-center gap-2">
                <Eye className="w-4 h-4 text-[#444746]" />
                <span className="text-xs font-bold text-gray-800">검증 완료 데이터 (Ground Truth)</span>
                <span className="text-[10px] text-gray-500">ID: {detailViewData.id}</span>
              </div>
              <button onClick={() => { setDetailViewId(null); setDetailViewData(null); }} className="p-1 hover:bg-gray-200 rounded">
                <X className="w-3.5 h-3.5 text-gray-500" />
              </button>
            </div>
            <div className="px-4 py-2 border-b border-gray-100 bg-gray-50/50 flex flex-wrap gap-3 text-[10px] text-gray-600">
              <span><b>거래처:</b> {detailViewData.vendor_name || '-'}</span>
              <span><b>문서유형:</b> {detailViewData.document_type}</span>
              <span><b>파일:</b> {detailViewData.file_name || '-'}</span>
              <span><b>행수:</b> {detailViewData.item_count || 0}</span>
              <span><b>수정:</b> {detailViewData.correction_count || 0}건</span>
              <span><b>품질:</b> {parseFloat(detailViewData.quality_score || 0).toFixed(0)}%</span>
            </div>
            <div className="flex-1 overflow-auto min-h-0 p-4">
              <pre className="text-[11px] font-mono bg-gray-900 text-green-400 rounded-lg p-4 overflow-auto max-h-[50vh] whitespace-pre-wrap">
                {(() => {
                  try {
                    const d = typeof detailViewData.verified_data === 'string' ? JSON.parse(detailViewData.verified_data) : detailViewData.verified_data;
                    return JSON.stringify(d, null, 2);
                  } catch { return String(detailViewData.verified_data); }
                })()}
              </pre>
              {detailViewData.field_corrections && (() => {
                const corrections = typeof detailViewData.field_corrections === 'string' ? JSON.parse(detailViewData.field_corrections) : detailViewData.field_corrections;
                if (!corrections || corrections.length === 0) return null;
                return (
                  <div className="mt-3">
                    <div className="text-[10px] font-bold text-gray-500 mb-1">수정 이력 ({corrections.length}건):</div>
                    <div className="space-y-1">
                      {corrections.map((c, i) => (
                        <div key={i} className="text-[10px] px-2 py-1 bg-[#f1f3f4] border border-[#dadce0] rounded font-mono">
                          <b>{c.field}</b>: <span className="text-[#5f6368] line-through">{c.before}</span> → <span className="text-[#1f1f1f] font-bold">{c.after}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
